import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from '@worthy-npm/worthy-common-ui-components';
import { Spinner } from '@worthy-npm/worthy-ui-components';
import LayoutViews from 'components/LayoutViews';
import { defaultValues, fields, interests } from 'constants/registration.constants';
import { FormEvent, useState } from 'react';
import { createPortal } from 'react-dom';
import { Controller, useForm } from 'react-hook-form';
import { registerBuyer } from 'services/api/registration';
import {
  Confirmation,
  ContactUs,
  Form,
  FormSection,
  Interest,
  InterestsWrapper,
  SectionTitle,
  SubTitle,
  Title,
} from './RegistrationForm.styles';

const RegistrationForm = () => {
  const {
    control,
    formState: { errors },
    setError,
    clearErrors,
    getValues,
  } = useForm({
    defaultValues,
    mode: 'onSubmit',
  });
  const [submitting, setSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const hasStateSelect = getValues().country === 'United States';

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const data = getValues();
    let hasMissingFields = false;

    const { jewelry, diamonds, watches, gemstones, ...rest } = data;

    Object.keys(rest).forEach((field) => {
      if (field === 'state' && !hasStateSelect) {
        return;
      }
      if (typeof rest[field] === 'string' && (rest[field] as string).length === 0) {
        setError(field, { message: 'Field cannot be empty' }, { shouldFocus: !hasMissingFields });
        hasMissingFields = true;
      }
    });

    if (!jewelry && !diamonds && !watches && !gemstones) {
      setError('jewelry', { message: 'Please choose at least one category' });
      hasMissingFields = true;
    }

    if (hasMissingFields) {
      return;
    }

    setSubmitting(true);
    const { hasErrors, errors } = await registerBuyer({ ...data, terms: true });

    if (!hasErrors) {
      window.location.reload();
      return;
    }
    setSubmitting(false);
    let focusCalled = false;
    Object.keys(errors).forEach((field) => {
      // @ts-ignore
      if (!errors[field]) {
        return;
      }
      // @ts-ignore
      setError(field, { message: errors[field] }, { shouldFocus: !focusCalled });
      focusCalled = true;
    });
  };

  let root = document.getElementById('new_buyer_registration');
  const isLocalDev =
    process.env.REACT_APP_ENV === 'local' && process.env.PUBLIC_URL === window.location.origin;
  const isTest = process.env.REACT_APP_ENV === 'test';

  if (isLocalDev || isTest) {
    root = document.body;
  }

  if (!root) {
    return null;
  }

  return createPortal(
    <LayoutViews>
      <Title data-automation={'buyer_registration_title'}>Registration</Title>
      <SubTitle data-automation={'buyer_registration_subtitle'}>
        Fill out this form and join us today!
      </SubTitle>

      <Form onSubmit={onSubmit}>
        <FormSection>
          <SectionTitle>Details</SectionTitle>
          {Object.values(fields).map((field) => {
            if (!hasStateSelect && field.name === 'state') {
              return null;
            }
            return (
              <FormControl
                sx={{
                  minWidth: 160,
                  maxWidth: 427,
                  width: '100%',
                }}
                margin="normal"
                key={field.name}
              >
                {['text', 'password'].includes(field.type) && (
                  <Controller
                    name={field.name}
                    control={control}
                    render={({ field: { ref, ...rest }, fieldState }) => (
                      // @ts-ignore
                      <TextField
                        {...rest}
                        label={field.label}
                        autoComplete={field.autocomplete || 'off'}
                        type={field.type}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message || ' '}
                        color="secondary"
                        inputProps={{
                          ref,
                        }}
                        onChange={(e) => {
                          clearErrors(field.name);
                          rest.onChange(e);
                        }}
                      />
                    )}
                  />
                )}
                {field.type === 'select' && (
                  <Controller
                    name={field.name}
                    control={control}
                    render={({ field: { ref, ...rest }, fieldState }) => (
                      <>
                        <InputLabel
                          id={field.name}
                          error={!!fieldState.error}
                          color="secondary"
                        >
                          {field.label}
                        </InputLabel>
                        <Select
                          labelId={field.name}
                          label={field.label}
                          {...rest}
                          onChange={(e) => {
                            clearErrors(field.name);
                            rest.onChange(e);
                          }}
                          error={!!fieldState.error}
                          color="secondary"
                          sx={{
                            maxHeight: '500px',
                          }}
                          inputProps={{
                            ref,
                          }}
                          MenuProps={{
                            sx: { maxHeight: '500px' },
                          }}
                        >
                          {field.options?.map((option) => {
                            return (
                              <MenuItem
                                value={option.value}
                                key={option.value}
                              >
                                {option.label}
                              </MenuItem>
                            );
                          })}
                        </Select>
                        <FormHelperText error>{fieldState.error?.message}</FormHelperText>
                      </>
                    )}
                  />
                )}
              </FormControl>
            );
          })}
        </FormSection>
        <FormSection>
          <SectionTitle>Select categories you are interested in</SectionTitle>
          <InterestsWrapper hasError={!!errors['jewelry']}>
            {interests.map((interest) => (
              <Controller
                name={interest}
                control={control}
                key={interest}
                render={({ field: { ref, onChange, ...rest } }) => (
                  <Interest>
                    <FormControlLabel
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        margin: 0,
                        width: '100%',
                        fontSize: '14px',
                      }}
                      labelPlacement="start"
                      label={interest}
                      control={
                        <Switch
                          {...rest}
                          onChange={(e) => {
                            clearErrors('jewelry');
                            onChange(e);
                          }}
                          color="secondary"
                        />
                      }
                    />
                  </Interest>
                )}
              />
            ))}
          </InterestsWrapper>
          <FormHelperText error>
            {errors['jewelry'] ? 'Please select at least 1 category' : ''}
          </FormHelperText>
        </FormSection>
        <FormSection>
          <Confirmation>
            <FormControlLabel
              control={
                <Checkbox
                  color="secondary"
                  checked={acceptedTerms}
                  onChange={(e) => {
                    setAcceptedTerms(e.target.checked);
                  }}
                  inputProps={{
                    // @ts-ignore
                    'data-automation': 'buyer_registration_terms',
                  }}
                />
              }
              label={
                <>
                  Approve Program{' '}
                  <a
                    href={`${process.env.REACT_APP_MONOLITH_URL}/buyers/about/terms`}
                    target="_blank"
                    rel="noreferrer"
                    className="terms-link"
                  >
                    Terms
                  </a>
                </>
              }
            />
            <Button
              color="secondary"
              variant="contained"
              type="submit"
              disabled={!acceptedTerms || submitting}
              data-automation={'buyer_registration_submit'}
            >
              {submitting ? <Spinner /> : 'Done'}
            </Button>
          </Confirmation>
        </FormSection>
      </Form>
      <ContactUs>
        Contact us at <a href="mailto:buyers@worthy.com">buyers@worthy.com</a>
      </ContactUs>
    </LayoutViews>,
    root,
  );
};

export default RegistrationForm;
